import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import _ from 'lodash';
import { lightTheme } from 'theme';
import MuiThemeProvider from '@material-ui/core/styles/MuiThemeProvider';

import { SectionWrapper, SectionGrid } from 'components/Section';
import FullHeightGrid from 'components/FullHeightGrid';
import ResourceCard from 'views/Resources/elements/ResourceCard';
import BlogCard from 'views/Blog/elements/BlogCard';
import PublicationItem from 'views/Publications/elements/PublicationItem';
import PublicationCard from 'views/Publications/elements/PublicationCard';
import ResponsiveImg from 'components/ResponsiveImg';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Container from 'components/Container';

const RelatedWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 10px 0 0 0;
  margin: 10px 0;
  &:not(:last-child) {
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    padding-bottom: 20px;
  }
`;

const RelatedImg = styled(ResponsiveImg)`
  img {
    border-radius: 5px;
    width: 60px;
    height: 60px;
  }
  margin-left: 10px;
  flex-shrink: 0;
  justify-self: flex-end;
`;

const RelatedLink = styled.a`
  display: block;
  text-decoration: none;
`;

const SlimRelatedItem = ({ title, label, url, image, ...other }) => (
  <RelatedWrapper {...other}>
    <RelatedLink href={url} target="_blank">
      <Typography variant="h5" gutterBottom>
        {title}
      </Typography>
      <Typography variant="overline" color="textSecondary">
        {label}
      </Typography>
    </RelatedLink>
    {image && image.url && (
      <RelatedImg src={image.url} defaultWidth={240} maxWidth={240} square />
    )}
  </RelatedWrapper>
);

const RelatedContent = ({ resource, blog, ...other }) => (
  <SectionGrid
    container
    size="small"
    spacing={40}
    data-swiftype-index="false"
    {...other}
  >
    {resource.length > 0 && (
      <Fragment>
        {resource.map((item, idx) => (
          <FullHeightGrid item key={idx} xs={12} sm>
            <ResourceCard resource={item} />
          </FullHeightGrid>
        ))}
      </Fragment>
    )}
    {blog.length > 0 && (
      <Fragment>
        {blog.map((post, idx) => (
          <FullHeightGrid item key={idx} xs={12} sm>
            <BlogCard post={post} />
          </FullHeightGrid>
        ))}
      </Fragment>
    )}
  </SectionGrid>
);

const MixedRelatedContent = ({ contentItems, ...other }) => (
  <SectionGrid
    container
    size="small"
    spacing={40}
    data-swiftype-index="false"
    {...other}
  >
    {contentItems.length > 0 && (
      <Fragment>
        {contentItems.map((item, idx) => (
          <FullHeightGrid item key={idx} xs={12} sm>
            {item._content_type_uid === 'resources_landing_page' && (
              <ResourceCard resource={item} />
            )}
            {item._content_type_uid === 'blog_pages' && (
              <BlogCard post={item} />
            )}
            {item._content_type_uid === 'publication_detail' && (
              <PublicationItem publication={item} />
            )}
            {item._content_type_uid === 'enhanced_publication_detail' && (
              <MuiThemeProvider theme={lightTheme}>
                <PublicationCard publication={item} />
              </MuiThemeProvider>
            )}
          </FullHeightGrid>
        ))}
      </Fragment>
    )}
  </SectionGrid>
);

const SlimMixedRelatedContent = ({ contentItems, ...other }) => (
  <div data-swiftype-index="false" {...other}>
    {contentItems.length > 0 && (
      <>
        {contentItems.map((item, idx) => (
          <Fragment key={idx}>
            {item._content_type_uid === 'help_article' && (
              <SlimRelatedItem
                title={item.title}
                label={`Help Article`}
                url={item.url}
              />
            )}
            {item._content_type_uid === 'enhanced_publication_detail' && (
              <SlimRelatedItem
                title={item.title}
                label={`Publication - ${_.get(item, 'details.journal') ||
                  _.get(item, 'pubmed_data.fulljournalname')}`}
                url={item.url}
              />
            )}
            {item._content_type_uid === 'publication_detail' && (
              <SlimRelatedItem
                title={item.title}
                label={`Publication - ${item.journal}`}
                url={item.link.href}
              />
            )}
            {item._content_type_uid === 'blog_pages' && (
              <SlimRelatedItem
                title={item.title}
                label={`The Bench Blog`}
                url={item.url}
                image={item.hero_image}
              />
            )}
            {item._content_type_uid === 'resources_landing_page' && (
              <SlimRelatedItem
                title={item.title}
                label={`Resource - ${item.resource_type}`}
                url={item.url}
                image={item.main_image}
              />
            )}
          </Fragment>
        ))}
      </>
    )}
  </div>
);

const RelatedContentWrapper = ({
  backgroundcolor,
  title,
  children,
  ...other
}) => (
  <SectionWrapper backgroundcolor={backgroundcolor} {...other}>
    <Container>
      <SectionGrid
        container
        size="small"
        spacing={40}
        justify="center"
        alignItems="stretch"
      >
        {title && (
          <Grid item xs={12}>
            <Typography variant="h3" align="center" gutterBottom>
              {title}
            </Typography>
          </Grid>
        )}
        {children}
      </SectionGrid>
    </Container>
  </SectionWrapper>
);

RelatedContent.defaultProps = {
  resource: [],
  blog: []
};

RelatedContent.propTypes = {
  resource: PropTypes.array,
  blog: PropTypes.array
};

MixedRelatedContent.defaultProps = {
  content: []
};

MixedRelatedContent.propTypes = {
  content: PropTypes.array
};

RelatedContentWrapper.defaultProps = {
  backgroundcolor: 'white',
  title: ''
};

RelatedContentWrapper.propTypes = {
  backgroundcolor: PropTypes.string,
  title: PropTypes.string
};

export {
  RelatedContent,
  SlimMixedRelatedContent,
  MixedRelatedContent,
  SlimRelatedItem,
  RelatedContentWrapper
};
